import KpiStatus from './status/KpiStatus';
import PortCalls from './portCalls/PortCalls';
import { useEffect } from 'react';

import { vesselPolygonCallsSelector, vesselStoppageEventsSelector } from '../../kpiSelectors';
import { kpisActions } from '../../kpiSlice';

import { useAppDispatch, useAppSelector } from '../../../../core/rootHooks';
import { AppDispatch, RootState } from '../../../../core/store';
import Voyages from './voyages/Voyages';
import StoppageEvents from './stoppageEvents/StoppageEvents';
import ShipyardVisits from './portCalls/ShipyardVisits';
import { IKpiVessel, TVoyages } from '../../kpiTypes';
import DynamicTab from '../../../../shared/components/tabs/DynamicTab';
import { selectFeatureDateRangeAnalysisDateRange } from '../../../date-range/slices/dateRangeSlice';

export interface IKpiDeploymentProps {
	vessel: IKpiVessel;
	vesselVoyages: TVoyages | null;
}

const KpiDeployment: React.FC<IKpiDeploymentProps> = ({ ...props }) => {
	const vesselPolygonCalls = useAppSelector((state: RootState) => vesselPolygonCallsSelector(state, props.vessel.imoNumber!));
	const vesselStoppageEvents = useAppSelector((state: RootState) => vesselStoppageEventsSelector(state, props.vessel.imoNumber!));
	const analysisDateRange = useAppSelector(selectFeatureDateRangeAnalysisDateRange);

	const dispatch = useAppDispatch<AppDispatch>();

	if (!props.vessel.imoNumber) {
		return;
	}

	if (!props.vesselVoyages) {
		return;
	}

	useEffect(() => {
		if (props.vessel.imoNumber) {
			const startDate = analysisDateRange.dateRange!.start.toDate('UTC').toISOString();
			const endDate = analysisDateRange.dateRange!.end.toDate('UTC').toISOString();
			dispatch(kpisActions.getVesselPolygonCallsRequestAction({ imoNumber: props.vessel.imoNumber, startDate, endDate }));
		}
	}, [dispatch, props.vessel.imoNumber, analysisDateRange]);

	useEffect(() => {
		if (props.vessel.imoNumber) {
			const startDate = analysisDateRange.dateRange!.start.toDate('UTC').toISOString();
			const endDate = analysisDateRange.dateRange!.end.toDate('UTC').toISOString();
			dispatch(kpisActions.getVesselStoppageEventsRequestAction({ imoNumber: props.vessel.imoNumber, startDate, endDate }));
		}
	}, [dispatch, props.vessel.imoNumber, analysisDateRange]);

	const tabs = [
		{
			id: 'status',
			label: 'Status',
			content: <KpiStatus vessel={props.vessel} />,
		},
		{
			id: 'portCalls',
			label: 'Port Calls',
			content:
				vesselPolygonCalls === null ? 'Loading' : <PortCalls vessel={props.vessel} vesselPolygonCalls={vesselPolygonCalls} />,
		},
		{
			id: 'voyages',
			label: 'Voyages',
			content: <Voyages vessel={props.vessel} vesselVoyages={props.vesselVoyages} />,
		},
		{
			id: 'shipyardVisits',
			label: 'Shipyard Visits',
			content: <ShipyardVisits vessel={props.vessel} vesselPolygonCalls={vesselPolygonCalls} />,
		},
		{
			id: 'stoppageEvents',
			label: 'Stoppage Events',
			content: <StoppageEvents vessel={props.vessel} vesselStoppageEvents={vesselStoppageEvents} isInSanctionArea={false} />,
		},
		{
			id: 'sanctionedAreas',
			label: 'Sanctioned Areas',
			content: <StoppageEvents vessel={props.vessel} vesselStoppageEvents={vesselStoppageEvents} isInSanctionArea={true} />,
		},
	];

	return (
		<DynamicTab
			items={tabs}
			key={props.vessel.imoNumber}
			tabColor="msi-darkgrey-color"
			props={{ isVertical: true }}
			giveSpace={false}
			isSubtab
		/>
	);
};

export default KpiDeployment;
